import { useState } from 'react';
import { useEffect } from 'react';
import { loadAdminRows, loadAdminStats, selectAdmin, setPaging, setSort } from './adminSlice';
import { removeUser } from "@/user/userSlice";
import { useAppDispatch, useAppSelector, type RootState } from '@/store';
import type { AdminStat, AdminRow } from './adminApi';
import { ChevronUp, ChevronDown, RefreshCw, LogOut } from 'lucide-react';
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';


export default function AdminPage() {
  const dispatch = useAppDispatch();
  const admin = useAppSelector(selectAdmin);
  const username = useAppSelector((s: RootState) => s.user.username);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!username) return;

    dispatch(loadAdminRows({ offset: admin.offset, limit: admin.limit }) as any);
    dispatch(loadAdminStats() as any);
    document.title = "SM AI: Messages"
  }, [dispatch, username]);

  function changePage(next: number) {
    const newOffset = Math.max(0, next * admin.limit);
    dispatch(setPaging({ offset: newOffset, limit: admin.limit }));
    dispatch(loadAdminRows({ offset: newOffset, limit: admin.limit }) as any);
  }

  function toggleSort(column: string) {
    const order = admin.sortBy === column && admin.order === 'asc' ? 'desc' : 'asc';
    dispatch(setSort({ sortBy: column, order }));
    dispatch(
      loadAdminRows({
        offset: admin.offset,
        limit: admin.limit,
        sortBy: column,
        order
      }) as any
    );
  }

  async function handleRefresh() {
    if (!username) return;
    setRefreshing(true);
    try {
      await dispatch(loadAdminStats()).unwrap();
      await dispatch(loadAdminRows({})).unwrap();
    } catch (err) { }
    setRefreshing(false);
  }

  function SortIcon({ column }: { column: string }) {
    const active = admin.sortBy === column;
    if (!active) return <ChevronUp size={14} className="opacity-0" />;

    return admin.order === 'asc' ? (
      <ChevronUp size={14} className="text-blue-600" />
    ) : (
      <ChevronDown size={14} className="text-blue-600" />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-blue-700 mb-4">
          <span className="text-black">SM AI:</span> Chat Messages
        </h1>
        <div className="flex items-center gap-4">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 w-10 h-10 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-800 text-white disabled:opacity-50 transition"
            title="Refresh">
            <RefreshCw size={18} />
          </button>
          <button
            onClick={() => { dispatch(removeUser()); }}
            className="p-2 w-10 h-10 flex items-center justify-center rounded-lg bg-slate-300 hover:bg-red-400 text-white transition"
            title="Logout">
            <LogOut size={18} />
          </button>
        </div>
      </div>
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {admin.stats?.filter((s) => s.input_tokens > 0 || s.output_tokens > 0).map((s: AdminStat, idx: number) => (
          <div
            key={idx}
            className="p-4 rounded-lg border bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="font-semibold text-md text-blue-700">
              {`Gemini ${s.default_thinking ? 'Flash' : 'Pro'}`}  {s.role === 'user' ? 'Free' : 'Tier 1'} {s.role === 'system' ? '(System)' : ''}
            </div>

            <div className="mt-2 text-xs text-slate-600">
              <span className="font-medium">Total Input Tokens</span>
              <div className="text-slate-500">
                {s.input_tokens.toLocaleString()}
                {' '}({(s.input_tokens / Math.max(1, s.input_count)).toFixed(2)}/query)
              </div>
            </div>
            <div className="text-xs text-slate-600 pt-2">
              <span className="font-medium">Total Output Tokens</span>
              <div className="text-slate-500">
                {s.output_tokens.toLocaleString()}
                {' '}({(s.output_tokens / Math.max(1, s.output_count)).toFixed(2)}/query)
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* === Table === */}
      {/* <section className="bg-white border rounded-lg shadow-sm overflow-auto max-h-[70vh]"> */}
      <section className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-blue-50 text-blue-700 border-b sticky top-0 z-20">
            <tr>
              {[
                { key: 'role', label: 'Sender' },
                { key: 'content', label: 'Message', sortable: false },
                { key: 'defaultThinking', label: 'Model' },
                { key: 'defaultContext', label: 'Context' },
                { key: 'inputToken', label: 'Input Tokens' },
                { key: 'outputToken', label: 'Output Tokens' },
                { key: 'sql_query', label: 'Data Query', sortable: false }
              ].map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 font-medium ${
                    col.sortable !== false ? 'cursor-pointer hover:bg-blue-100' : ''
                  }`}
                  onClick={() =>
                    col.sortable !== false && toggleSort(col.key)
                  }
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && <SortIcon column={col.key} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y">
            {admin.rows.map((r: AdminRow, i: number) => (
              <tr key={i} className="bg-white hover:bg-slate-50 transition-colors">
                <td className="px-4 py-2">{r.role}{r.display_name ? `: ${r.display_name}` : ""}</td>

                <td className="px-4 py-2 max-w-xs">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {r.content}
                  </ReactMarkdown>
                </td>

                <td className="px-4 py-2">
                  {r.default_thinking ? 'Default' : 'Long'}
                </td>

                <td className="px-4 py-2">
                  {r.default_context ? 'Data Only' : 'More Context'}
                </td>

                <td className="px-4 py-2">{r.input_token.toLocaleString()}</td>
                <td className="px-4 py-2">{r.output_token.toLocaleString()}</td>

                <td className="px-4 py-2 max-w-xs">
                  <pre className="whitespace-pre-wrap text-xs text-slate-600 truncate">
                    {r.sql_query ?? '-'}
                  </pre>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="flex items-center justify-between mt-6">
        <div className="flex gap-2">
          <button
            onClick={() => changePage(Math.max(0, admin.offset / admin.limit - 1))}
            className="px-3 py-1 rounded-md border text-blue-700 hover:bg-blue-50 disabled:opacity-40"
            disabled={admin.offset === 0}
          >
            Prev
          </button>

          <button
            onClick={() => changePage(admin.offset / admin.limit + 1)}
            className="px-3 py-1 rounded-md border text-blue-700 hover:bg-blue-50"
          >
            Next
          </button>
        </div>

        <div className="text-sm text-slate-500">{admin.rows.length} rows</div>
      </div>
    </div>
  );
}
